import {
  FEATURES,
  GROUP_TYPE,
  IBroadcastInfo,
  IGroupInfo,
  IUserInfo,
  USER_TYPE,
} from "@home-health-notify/domain";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItemAvatar,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  OrganizationContext,
  UserDirectoryContext,
  useTranslation,
} from "hhn-commons";
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router";
import DisplayAvatarImage from "../../../common/DisplayAvatarImage";
import { SearchBox } from "../../../common/SearchBox";
import { RightDrawerContext } from "../../../context/RightDrawerContext";
import { ROUTES } from "../../../routes";
import {
  GroupMembersHeader,
  MemberListItem,
  MemberListItemButton,
  MembersWrapper,
} from "./Members.style";
import MembersByType from "./MembersByType";
import MembersPendingReferrals from "./MembersPendingReferrals";

export const Members = ({
  groupInfo,
}: {
  groupInfo: IGroupInfo | IBroadcastInfo;
}) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "groupChatInfo" });
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { openAddMembersDrawer } = useContext(RightDrawerContext);
  const { userDirectory } = useContext(UserDirectoryContext);
  const currentOrganization = selectedOrgUser!.getOrganization();
  const orgId = currentOrganization!.organizationId;
  const currentUser = currentOrganization!.userProfile;
  const isBroadcastGroup = groupInfo.groupType === GROUP_TYPE.BROADCAST;
  const isAddMembersAllowed = selectedOrgUser!.hasAccess(FEATURES.ADD_MEMBERS);

  const navigate = useNavigate();
  const goToChat = (userInfo: IUserInfo) => {
    navigate(`../${orgId}/${ROUTES.CHATS}/private/${userInfo.id}/message`);
  };

  const [isSearchMode, setIsSearchMode] = useState(false); // State for search query
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const doesGroupHaveMembers =
    groupInfo.members && groupInfo.members.length > 0;

  const sortedMembers = doesGroupHaveMembers
    ? (groupInfo.members || []).sort((a, b) => {
        const userA = userDirectory.getUserInfo(a.userId);
        const userB = userDirectory.getUserInfo(b.userId);
        return userA.name.localeCompare(userB.name);
      })
    : [];

  // Filter members based on search query
  const filteredMembers = sortedMembers.filter(member => {
    const userInfo = userDirectory.getUserInfo(member.userId);
    return userInfo.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const openAddMembers = () => {
    openAddMembersDrawer(groupInfo, true);
  };

  const activeUserDirectory = userDirectory.getActiveUserDirectory();

  return (
    <MembersWrapper data-testid="members-component">
      {doesGroupHaveMembers && (
        <>
          {!isBroadcastGroup && (
            <MembersPendingReferrals
              groupInfo={groupInfo as IGroupInfo}
              goToChat={goToChat}
            />
          )}
          <GroupMembersHeader>
            <Typography data-testid="members-header">
              {tNs("groupMembers")} ({(groupInfo.members || []).length})
            </Typography>
            <Box>
              <IconButton onClick={() => setIsSearchMode(!isSearchMode)}>
                <Tooltip title={tNs("search")}>
                  <SearchIcon />
                </Tooltip>
              </IconButton>
              {(groupInfo.isMember || isBroadcastGroup) &&
                isAddMembersAllowed && (
                  <Tooltip title={tNs("addMembers")}>
                    <IconButton
                      onClick={openAddMembers}
                      data-testid="add-members-icon-button"
                    >
                      <PersonAddAlt1Icon />
                    </IconButton>
                  </Tooltip>
                )}
            </Box>
          </GroupMembersHeader>
          {isSearchMode && (
            <Box
              sx={{
                ml: 2,
                mr: 2
              }}>
              <SearchBox
                autoFocus={true}
                searchText={searchQuery}
                onSearchTextChange={(searchText: string) => {
                  setSearchQuery(searchText);
                }}
                onSearchClear={() => setSearchQuery("")}
              />
            </Box>
          )}
          <Divider />
          <Box>
            <List sx={{ paddingTop: "0px" }} data-testid="members-list">
              {groupInfo.isMember && (
                <MemberListItem key={currentUser.id} disablePadding>
                  <MemberListItemButton style={{ paddingLeft: 12 }}>
                    <Box sx={{
                      flexDirection: "column"
                    }}>
                      <ListItemAvatar sx={{ marginLeft: "10px" }}>
                        <DisplayAvatarImage
                          sx={{ marginLeft: "10px", marginRight: "15px" }}
                          src={currentUser?.image}
                          alt={currentUser?.name}
                        />
                      </ListItemAvatar>
                    </Box>
                    <Box>
                      <Typography>
                        {currentUser?.name} ({tNs("me")})
                      </Typography>
                    </Box>
                  </MemberListItemButton>
                </MemberListItem>
              )}

              <MembersByType
                types={[USER_TYPE.PATIENT]}
                header={tNs("patients")}
                allMembers={filteredMembers} // Use filtered members
                userDirectory={activeUserDirectory}
                goToChat={goToChat}
                groupInfo={groupInfo}
              />
              <MembersByType
                types={[USER_TYPE.PHYSICIAN]}
                header={tNs("physicians")}
                allMembers={filteredMembers} // Use filtered members
                userDirectory={activeUserDirectory}
                goToChat={goToChat}
                groupInfo={groupInfo}
              />
              <MembersByType
                types={[USER_TYPE.USER]}
                header={tNs("clinicians")}
                allMembers={filteredMembers} // Use filtered members
                userDirectory={activeUserDirectory}
                goToChat={goToChat}
                groupInfo={groupInfo}
              />
              <MembersByType
                types={[USER_TYPE.STAFF, USER_TYPE.ADMIN]}
                header={tNs("office")}
                allMembers={filteredMembers} // Use filtered members
                userDirectory={activeUserDirectory}
                goToChat={goToChat}
                groupInfo={groupInfo}
              />
            </List>
          </Box>
        </>
      )}
    </MembersWrapper>
  );
};
